/**
 * AirCap Logic Service
 *
 * API service for Airport Capacity Intelligence module.
 * Uses the shared `api` instance (baseURL = /api/v1) to avoid double-prefix bugs.
 */

import api from './api';
import type {
    DDFSSchedule,
    ParameterSet,
    CapacityAnalysis,
    AnalysisSummary,
    SubsystemResult,
    SubsystemType,
    CalculationRequest,
    FormulaReference,
} from '../types/aircapLogic';

export const aircapLogicService = {
    // ==================== DDFS OPERATIONS ====================

    async uploadDDFS(file: File, loadFactor: number = 0.82): Promise<DDFSSchedule> {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post<DDFSSchedule>(
            `/aircap-logic/ddfs/upload?load_factor=${loadFactor}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return data;
    },

    /**
     * Build a DDFSSchedule from a project's stored fts_flights (IATA Busy Day).
     * Registers the schedule in the backend so it can immediately be used with runAnalysis.
     */
    async createDDFSFromProject(projectId: string, loadFactor: number = 0.85): Promise<DDFSSchedule> {
        const { data } = await api.post<DDFSSchedule>(
            `/aircap-logic/ddfs/from-project/${projectId}?load_factor=${loadFactor}`
        );
        return data;
    },

    async getSchedule(scheduleId: string): Promise<DDFSSchedule> {
        const { data } = await api.get<DDFSSchedule>(`/aircap-logic/ddfs/${scheduleId}`);
        return data;
    },

    async listSchedules(): Promise<DDFSSchedule[]> {
        const { data } = await api.get<DDFSSchedule[]>(`/aircap-logic/ddfs`);
        return data;
    },

    async generateSampleDDFS(): Promise<DDFSSchedule> {
        const { data } = await api.post<DDFSSchedule>(`/aircap-logic/ddfs/sample`);
        return data;
    },

    // ==================== PARAMETER OPERATIONS ====================

    async getDefaults(): Promise<ParameterSet> {
        const { data } = await api.get<ParameterSet>(`/aircap-logic/parameters/defaults`);
        return data;
    },

    // ==================== ANALYSIS OPERATIONS ====================

    async runAnalysis(request: CalculationRequest): Promise<CapacityAnalysis> {
        const { data } = await api.post<CapacityAnalysis>(`/aircap-logic/calculate`, request);
        return data;
    },

    async getAnalysis(analysisId: string): Promise<CapacityAnalysis> {
        const { data } = await api.get<CapacityAnalysis>(`/aircap-logic/analysis/${analysisId}`);
        return data;
    },

    async getAnalysisSummary(analysisId: string): Promise<AnalysisSummary> {
        const { data } = await api.get<AnalysisSummary>(`/aircap-logic/analysis/${analysisId}/summary`);
        return data;
    },

    async getSubsystemResult(analysisId: string, subsystemType: SubsystemType): Promise<SubsystemResult> {
        const { data } = await api.get<SubsystemResult>(
            `/aircap-logic/analysis/${analysisId}/subsystem/${subsystemType}`
        );
        return data;
    },

    // ==================== REPORT OPERATIONS ====================

    async downloadReport(analysisId: string, includeFormulas: boolean = true, language: string = 'en'): Promise<Blob> {
        const { data } = await api.get(
            `/aircap-logic/analysis/${analysisId}/report`,
            { params: { include_formulas: includeFormulas, language }, responseType: 'blob' }
        );
        return data;
    },

    // ==================== FORMULA REFERENCE ====================

    async getFormulas(): Promise<FormulaReference[]> {
        const { data } = await api.get<FormulaReference[]>(`/aircap-logic/formulas`);
        return data;
    },

    async getSubsystems(): Promise<{ id: string; name: string }[]> {
        const { data } = await api.get<{ id: string; name: string }[]>(`/aircap-logic/subsystems`);
        return data;
    },
};

export default aircapLogicService;
